/**
 * Where the fighter must not be, tick by tick (docs/spec/02 §2, §6; 03 §5): a grid of ship
 * positions against time, marked wherever a bomb, an enemy, a held beam or the lance would have it.
 * Positions are the steering lattice's: a tick's flight apart, centred on where the ship starts.
 */

import * as sim from '../sim';
import type { GameState } from '../sim';
import type { Forecast } from './forecast';
import * as horizon from './horizon';

// Local copies (see horizon.ts).
const { BEAM, BOMB, FIELD, FIGHTER, NOVA } = sim;
const { COLS, HORIZON, LETHAL, PACE, STRIDE } = horizon;

/** The price of a near miss. */
const NEAR = 6;
/** Pixels kept clear beyond the engine's own hit boxes. */
const BOMB_MARGIN = 1.5;
const ENEMY_MARGIN = 2.5;
const BEAM_MARGIN = 3;
const LANCE_MARGIN = 2;
/** How much wider the near-miss band is. */
const NEAR_BAND = 5;

const ROW = FIELD.fighterY;
/** A bomb is level with the ship within this of its row, and strikes within `BOMB_HALF` of it. */
const BOMB_REACH = BOMB.halfH + FIGHTER.hitH + 1;
const BOMB_HALF = BOMB.halfW + FIGHTER.hitW + BOMB_MARGIN;
/** An enemy rams with a box a pixel smaller than its own: this is added to half that box. */
const RAM = FIGHTER.hitW - 1 + ENEMY_MARGIN;
const LANCE_HALF = NOVA.lanceHalf + FIGHTER.hitW + LANCE_MARGIN;

export class Danger {
  /** `cells[k * COLS + j + HORIZON]`: the ship at `x + j * PACE` after `k` ticks. */
  readonly cells = new Uint8Array(STRIDE * COLS);
  /** The lattice's centre, and whether there are two ships to keep clear. */
  x = 0;
  dual = false;
  /** The last tick with anything marked; 0 for a clear sky. */
  last = 0;
  /** The tick from which a rescued ship will be flying at the fighter's side; past the horizon for never. */
  private mateFrom = HORIZON + 1;

  /** For a ship at `x`. `slip`: how many ticks out the bot's eye is for bombs and enemies in flight. */
  build(s: GameState, f: Forecast, x: number, slip: number): void {
    if (this.last > 0) this.cells.fill(0);
    this.last = 0;
    this.x = x;
    this.dual = s.player.dual;
    // A rescued ship on its way down docks on one side of the fighter or the other: from about when
    // it lands, both sides are to be kept clear.
    const r = s.rescue;
    const docking = r !== null && r.phase === 'fly' && !this.dual;
    this.mateFrom = docking ? Math.max(1, Math.ceil((ROW - r.y) / BEAM.rescueSpeed) - 2) : HORIZON + 1;
    // A ship that is safe (a burst shield, a Nova) is passed through until the safety runs out.
    const from = Math.max(1, s.player.safe);
    if (from > HORIZON) return;
    this.bombs(s, f, from, slip);
    this.enemies(f, from, slip);
    this.beams(s, f);
    this.lance(f, from);
  }

  at(k: number, j: number): number {
    return this.cells[k * COLS + j + HORIZON] ?? 0;
  }

  /** Something `half` wide at `x` is at the fighter's row after `k` ticks. */
  private mark(k: number, x: number, half: number): void {
    if (this.dual) {
      this.span(k, x - FIGHTER.dualOffset, half);
      this.span(k, x + FIGHTER.dualOffset, half);
    } else this.span(k, x, half);
    if (k >= this.mateFrom) {
      // Whichever side it takes, the ship beside the fighter can be hit too.
      this.span(k, x - 2 * FIGHTER.dualOffset, half);
      this.span(k, x + 2 * FIGHTER.dualOffset, half);
    }
  }

  private span(k: number, x: number, half: number): void {
    const row = k * COLS + HORIZON;
    const near = half + NEAR_BAND;
    const lo = Math.max(-HORIZON, Math.ceil((x - near - this.x) / PACE));
    const hi = Math.min(HORIZON, Math.floor((x + near - this.x) / PACE));
    if (hi >= lo && k > this.last) this.last = k;
    for (let j = lo; j <= hi; j++) {
      const level = Math.abs(this.x + PACE * j - x) <= half ? LETHAL : NEAR;
      if ((this.cells[row + j] ?? 0) < level) this.cells[row + j] = level;
    }
  }

  /** Bombs fly straight (02 §5). */
  private bombs(s: GameState, f: Forecast, from: number, slip: number): void {
    for (const b of s.bombs) {
      if (b.vy <= 0) continue;
      // The hive moves between which it is level with the ship.
      const first = Math.ceil((ROW - BOMB_REACH - b.y) / b.vy);
      const last = Math.floor((ROW + BOMB_REACH - b.y) / b.vy);
      for (let k = from; k <= HORIZON; k++) {
        const u = Math.max(0, (f.adv[k] ?? 0) + slip);
        if (u < first) continue;
        if (u > last) break;
        this.mark(k, b.x + b.vx * u, BOMB_HALF);
      }
    }
  }

  /** Anything in the air that reaches the fighter's row rams it (02 §8). */
  private enemies(f: Forecast, from: number, slip: number): void {
    const t = f.tracks;
    for (let i = 0; i < t.count; i++) {
      const base = i * STRIDE;
      const half = (t.half[i] ?? 6) + RAM;
      for (let k = from; k <= HORIZON; k++) {
        const seen = base + Math.max(0, Math.min(HORIZON, k + slip));
        const y = t.y[seen] ?? 0;
        if (y > 0 && Math.abs(y - ROW) <= half) this.mark(k, t.x[seen] ?? 0, half);
      }
    }
  }

  /** A held beam takes a lone fighter anywhere in its cone (02 §6). */
  private beams(s: GameState, f: Forecast): void {
    if (s.player.dual) return;
    const beam = s.beam;
    if (beam) {
      // Hive moves from now during which the beam is held.
      const opening = beam.phase === 'extend' ? BEAM.extend - beam.t : 0;
      const held = beam.phase === 'extend' ? BEAM.hold : beam.phase === 'hold' ? BEAM.hold - beam.t : 0;
      this.cone(f, beam.x, beam.y, opening + 1, opening + held);
      return;
    }
    // A Monarch on a beam run: the cone will open where its path ends.
    for (const e of s.enemies) {
      if (e.gone || e.after !== 'beam' || (e.state !== 'hover' && e.state !== 'diving')) continue;
      const path = e.state === 'diving' ? e.path : null;
      const wait = path ? Math.ceil(Math.max(0, path.length - e.dist) / Math.max(0.1, e.speed)) + 1 : 1;
      const x = path ? (path.xs[path.length] ?? e.x) : e.x;
      const y = path ? (path.ys[path.length] ?? e.y) : e.y;
      this.cone(f, x, y + 8, wait + BEAM.extend + 1, wait + BEAM.extend + BEAM.hold);
    }
  }

  /** A cone whose top is at (x, y), held while the hive's move count is from `first` to `last`. */
  private cone(f: Forecast, x: number, y: number, first: number, last: number): void {
    const rel = (ROW - y) / BEAM.length;
    if (rel < 0 || rel > 1.12 || last < first) return;
    const half = BEAM.topHalf + (BEAM.bottomHalf - BEAM.topHalf) * Math.min(1, rel) + BEAM_MARGIN;
    for (let k = 1; k <= HORIZON; k++) {
      const u = f.adv[k] ?? 0;
      if (u > last) break;
      if (u < first) continue;
      // The beam looks at where the ship was before this tick's move.
      if (k > 1) this.span(k - 1, x, half);
      this.span(k, x, half);
    }
  }

  private lance(f: Forecast, from: number): void {
    if (f.lanceTo < f.lanceFrom) return;
    for (let k = from; k <= HORIZON; k++) {
      const u = f.adv[k] ?? 0;
      if (u > f.lanceTo) break;
      // A tick early: better out from under it before it lights.
      if (u >= f.lanceFrom - 1) this.mark(k, f.lanceX, LANCE_HALF);
    }
  }
}
